import type { GenerationImpactScenarioInput } from '../../types/generationImpact';

export const GRID_SUPPLY_GENERATION_SIZING_NOT_APPLICABLE =
  'Fuel-generation sizing does not apply to an all-grid supply scenario. Review the declared grid feeds, delivered capacity and contingency groups in the supply duty.';

/** A declared grid envelope is screened as entered, never resized into utility commitments. */
export function isGridImportOnlyScenario(input: GenerationImpactScenarioInput): boolean {
  return input.units.length > 0 && input.units.every(unit => unit.technology === 'grid-import');
}

export const GENERATION_SIZING_PHYSICAL_INPUT_ERRORS = Object.freeze({
  heatRate: 'Generation block HHV heat rates must be finite and greater than zero.',
  forcedOutageRate: 'Provided forced-outage rates must be finite and between zero and one.',
  nox: 'Provided NOx emission factors must be finite and non-negative.',
  co: 'Provided CO emission factors must be finite and non-negative.',
  ccsMode: 'CCS enabled mode must be boolean.',
});

/**
 * Fail-closed physical bounds shared by every uniform generation sizing path.
 * Error order is stable so UI, report, and server callers expose the same basis.
 */
export function validateGenerationSizingPhysicalInputs(
  input: GenerationImpactScenarioInput,
): readonly string[] {
  const errors: string[] = [];
  if (input.units.some(
    (unit) => !Number.isFinite(unit.heatRateBtuPerKWhHhv) || unit.heatRateBtuPerKWhHhv <= 0,
  )) {
    errors.push(GENERATION_SIZING_PHYSICAL_INPUT_ERRORS.heatRate);
  }
  if (input.units.some((unit) => (
    unit.forcedOutageRate != null
    && (!Number.isFinite(unit.forcedOutageRate)
      || unit.forcedOutageRate < 0
      || unit.forcedOutageRate > 1)
  ))) {
    errors.push(GENERATION_SIZING_PHYSICAL_INPUT_ERRORS.forcedOutageRate);
  }
  if (input.units.some((unit) => (
    unit.noxLbPerMWh != null
    && (!Number.isFinite(unit.noxLbPerMWh) || unit.noxLbPerMWh < 0)
  ))) {
    errors.push(GENERATION_SIZING_PHYSICAL_INPUT_ERRORS.nox);
  }
  if (input.units.some((unit) => (
    unit.coLbPerMWh != null
    && (!Number.isFinite(unit.coLbPerMWh) || unit.coLbPerMWh < 0)
  ))) {
    errors.push(GENERATION_SIZING_PHYSICAL_INPUT_ERRORS.co);
  }
  if (typeof input.ccs.enabled !== 'boolean') {
    errors.push(GENERATION_SIZING_PHYSICAL_INPUT_ERRORS.ccsMode);
  }
  return errors;
}
